import { type JSX, lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { getEnvironment } from '~/config';
import { AuthProvider } from '~/contexts/AuthContext';
import { ConnectivityProvider } from '~/contexts/ConnectivityContext';
import { RefreshProvider } from '~/contexts/RefreshContext';
import { TenantProvider } from '~/contexts/TenantContext';
import { ThemeProvider } from '~/contexts/ThemeContext';

import { ErrorBoundary } from '~/components/ErrorBoundary';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { ProtectedLayout } from '~/components/ProtectedLayout';
import { ProtectedRoute } from '~/components/ProtectedRoute';
import { Dashboard } from '~/pages/Dashboard';
import { DebugConfig } from '~/pages/DebugConfig';
import { DocPage, DocsHome, DocsLayout } from '~/pages/Documentation';
import { Invitation } from '~/pages/Invitations';
import { JiraLayout } from '~/pages/Jira';
import { Login } from '~/pages/Login';
import { MCPAuthCallback } from '~/pages/MCPAuthCallback';
import { NotFound } from '~/pages/NotFound';
import { OAuthConsent } from '~/pages/OAuthConsent';
import { OAuthRedirect } from '~/pages/OAuthRedirect';
import { Onboarding } from '~/pages/Onboarding/Onboarding';
import {
  DangerZone,
  Organization,
  OrganizationDomains,
  OrganizationMembers,
  OrganizationSettings,
  OrganizationTeam,
  OrganizationTeams,
} from '~/pages/Organization';
import {
  FrameworkPage,
  FrameworksHome,
  PatternPage,
  PatternsHome,
  PatternsLayout,
  RefArchHome,
  RefArchPage,
} from '~/pages/Patterns';
import { ProductsLayout } from '~/pages/Products';
import { Projects } from '~/pages/Projects';
import { PRDReviewPage } from '~/pages/Projects/PRDReview';
import { RepositoriesLayout } from '~/pages/Repositories';
import { InvitationsSettings, SettingsLayout } from '~/pages/Settings';

// Lazy-loaded settings sections (code-split for performance)
const ProfileSection = lazy(() =>
  import('~/pages/Settings/ProfileSection').then(m => ({ default: m.ProfileSection })),
);
const PreferencesSection = lazy(() =>
  import('~/pages/Settings/PreferencesSection').then(m => ({ default: m.PreferencesSection })),
);
const SecuritySection = lazy(() =>
  import('~/pages/Settings/SecuritySection').then(m => ({ default: m.SecuritySection })),
);
const IntegrationsSection = lazy(() =>
  import('~/pages/Settings/IntegrationsSection').then(m => ({ default: m.IntegrationsSection })),
);
const TokensSection = lazy(() =>
  import('~/pages/Settings/TokensSection').then(m => ({ default: m.TokensSection })),
);
import { VerifyEmail } from '~/pages/VerifyEmail';

export default function App(): JSX.Element {
  const { isDevelopment } = getEnvironment();

  return (
    <Sentry.ErrorBoundary fallback={<div className="p-4 text-error">An error has occurred</div>}>
      <ErrorBoundary>
        <ThemeProvider>
          <Router>
            <AuthProvider>
              <ConnectivityProvider>
                <TenantProvider>
                  <RefreshProvider defaultInterval={30000}>
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      <Route path="/mcp-auth" element={<MCPAuthCallback />} />
                      <Route path="/oauth/consent" element={<OAuthConsent />} />
                      <Route path="/oauth/redirect" element={<OAuthRedirect />} />
                      <Route path="/invitations/:id" element={<Invitation />} />

                      {/* Protected without layout (onboarding has its own layout) */}
                      <Route
                        path="/onboarding"
                        element={
                          <ProtectedRoute>
                            <Onboarding />
                          </ProtectedRoute>
                        }
                      />

                      {/* Protected routes with shared layout */}
                      <Route element={<ProtectedLayout />}>
                        <Route index element={<Navigate to="docs/getting-started" replace />} />
                        <Route
                          path="beta"
                          element={<Navigate to="/docs/getting-started" replace />}
                        />
                        <Route path="dashboard" element={<Dashboard />} />
                        <Route path="projects" element={<Projects />}>
                          <Route index element={null} />
                          <Route path=":projectId" element={null} />
                          <Route path=":projectId/review" element={<PRDReviewPage />} />
                        </Route>
                        {/* Documentation routes */}
                        <Route path="docs" element={<DocsLayout />}>
                          <Route index element={<DocsHome />} />
                          <Route path=":sectionSlug" element={<DocPage />} />
                          <Route path=":sectionSlug/:subSlug" element={<DocPage />} />
                        </Route>

                        {/* Design patterns browser */}
                        <Route path="patterns" element={<PatternsLayout />}>
                          <Route index element={<PatternsHome />} />
                          <Route path="architectures" element={<RefArchHome />} />
                          <Route path="architecture/:refArchId" element={<RefArchPage />} />
                          <Route path="frameworks" element={<FrameworksHome />} />
                          <Route path="framework/:frameworkId" element={<FrameworkPage />} />
                          <Route path=":patternId" element={<PatternPage />} />
                        </Route>
                        <Route
                          path="debug/config"
                          element={isDevelopment ? <DebugConfig /> : <Navigate to="/projects" />}
                        />
                        {/* Backward-compatible redirect */}
                        <Route
                          path="profile"
                          element={<Navigate to="/settings/profile" replace />}
                        />

                        {/* Settings routes with sidebar layout */}
                        <Route path="settings" element={<SettingsLayout />}>
                          <Route index element={<Navigate to="profile" replace />} />
                          <Route
                            path="profile"
                            element={
                              <Suspense fallback={<LoadingSpinner />}>
                                <ProfileSection />
                              </Suspense>
                            }
                          />
                          {/* Agent Preferences moved to the top-level /agent-preferences
                              route (shell Account nav). Redirect the old settings path so
                              existing links/bookmarks keep working. */}
                          <Route
                            path="preferences"
                            element={<Navigate to="/agent-preferences" replace />}
                          />
                          <Route
                            path="security"
                            element={
                              <Suspense fallback={<LoadingSpinner />}>
                                <SecuritySection />
                              </Suspense>
                            }
                          />
                          <Route
                            path="integrations"
                            element={
                              <Suspense fallback={<LoadingSpinner />}>
                                <IntegrationsSection />
                              </Suspense>
                            }
                          />
                          <Route
                            path="tokens"
                            element={
                              <Suspense fallback={<LoadingSpinner />}>
                                <TokensSection />
                              </Suspense>
                            }
                          />
                          <Route path="invitations" element={<InvitationsSettings />} />
                        </Route>

                        {/* Agent Preferences — promoted out of the Settings view into the
                            shell's Account nav; renders standalone (no Settings sub-sidebar). */}
                        <Route
                          path="agent-preferences"
                          element={
                            <Suspense fallback={<LoadingSpinner />}>
                              <div className="mx-auto max-w-6xl px-8 py-8">
                                <PreferencesSection />
                              </div>
                            </Suspense>
                          }
                        />

                        <Route path="repositories" element={<RepositoriesLayout />} />
                        {/* Product selection lives in the URL (not uiStore) so it survives
                            reload — see ProductView.tsx. index/:productId render nothing
                            themselves; ProductsLayout reads the id via useParams (match.params
                            is shared/cumulative across the whole matched branch, confirmed
                            against react-router's matchRouteBranch) and renders ProductGrid at
                            the index route or ProductView's full-page Kanban board at
                            :productId. */}
                        <Route path="products" element={<ProductsLayout />}>
                          <Route index element={null} />
                          <Route path=":productId" element={null} />
                        </Route>
                        <Route path="jira" element={<JiraLayout />} />

                        {/* Organization routes with sub-layout */}
                        <Route path="org" element={<Organization />}>
                          <Route index element={<Navigate to="settings" replace />} />
                          <Route path="settings" element={<OrganizationSettings />} />
                          <Route path="members" element={<OrganizationMembers />} />
                          <Route path="teams" element={<OrganizationTeams />} />
                          <Route path="teams/:teamId" element={<OrganizationTeam />} />
                          <Route path="domains" element={<OrganizationDomains />} />
                          <Route path="danger-zone" element={<DangerZone />} />
                        </Route>
                      </Route>

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </RefreshProvider>
                </TenantProvider>
              </ConnectivityProvider>
            </AuthProvider>
          </Router>
        </ThemeProvider>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}
